import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, TextField, InputAdornment, Select, MenuItem } from '@mui/material';
import CustomSelect2 from './CustomSelect2';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';
import TableCellRate from './TableCellRate';
import TableCellText from './TableCellText';

const PacificCrossPrimaryCarePlan_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, loadings, setLoadings, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  pacificCrossPrimaryCarePlanSpecific, setPacificCrossPrimaryCarePlanSpecific,
  insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {

  const id = 'pacific_cross_primary_care_plan';

  const containerRef = useRef(null);

  const config = appConfig?.content?.specifics?.pacific_cross_primary_care_plan || {};

  const family_discounts = config['Family Discounts'] || {};
  const payment_frequency_charges = config['Payment Frequency Charges'] || {};

  const specific_deductibles = appConfig?.content?.deductibles?.pacific_cross_primary_care_plan?.['USD'] || [];

  // Core and Core_wOP are not quoted at a Nil deductible on the rate sheet.
  function getDeductibleItems(plan) {
    if (plan === 'core' || plan === 'core_with_op') {
      return specific_deductibles.filter(deductible => deductible !== 'Nil');
    }

    return specific_deductibles;
  }

  // The rate sheet only holds a Worldwide incl / excl USA split - every other
  // territory difference comes from the client's country of residence zone.
  const AREA_OF_COVERAGE_WITH_USA = 'Worldwide incl USA';
  const AREA_OF_COVERAGE_WITHOUT_USA = 'Worldwide excl USA';

  // What TableCellRate prints as a dash - shown until the rates are fetched.
  const NO_RATE = '-';

  // Semi-private room is a Hong Kong specific option - the rate provider quotes every other residence at Private regardless.
  const hasHongKongClient = clients.some(client => client.country_of_residence === 'Hong Kong');

  // Core is quoted inpatient only, every other plan is quoted as a total (IP + OP).
  const plans = [
    { planId: 'pacific_cross_primary_care_plan_core', plan: 'core', label: 'Core' },
    { planId: 'pacific_cross_primary_care_plan_core_with_op', plan: 'core_with_op', label: 'Core_wOP' },
    { planId: 'pacific_cross_primary_care_plan_pulse', plan: 'pulse', label: 'Pulse' },
    { planId: 'pacific_cross_primary_care_plan_complete', plan: 'complete', label: 'Complete' }
  ];

  const [optionalBenefits, setOptionalBenefits] = useState({
    maternity_5000: false,
    maternity_10000: false,
    dental: false,
    optical: false
  });

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts[id], loadings[id], optionalBenefits, clients.length]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'core_deductible', deductibles[id]?.core);
      addSelectedOption(id, 'core_with_op_deductible', deductibles[id]?.core_with_op);
      addSelectedOption(id, 'pulse_deductible', deductibles[id]?.pulse);
      addSelectedOption(id, 'complete_deductible', deductibles[id]?.complete);
      addSelectedOption(id, 'room_type', pacificCrossPrimaryCarePlanSpecific['room_type']);
      addSelectedOption(id, 'high_cost_country_plan', pacificCrossPrimaryCarePlanSpecific['high_cost_country_plan']);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
      addSelectedOption(id, 'maternity_5000', optionalBenefits.maternity_5000);
      addSelectedOption(id, 'maternity_10000', optionalBenefits.maternity_10000);
      addSelectedOption(id, 'dental', optionalBenefits.dental);
      addSelectedOption(id, 'optical', optionalBenefits.optical);
    }, 1000);
  }, [specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id],
  deductibles[id]?.core, deductibles[id]?.core_with_op, deductibles[id]?.pulse, deductibles[id]?.complete,
  pacificCrossPrimaryCarePlanSpecific['room_type'], pacificCrossPrimaryCarePlanSpecific['high_cost_country_plan'],
  discounts[id], loadings[id], optionalBenefits]);

  useEffect(() => {
    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'core_deductible', (option_value) => {
      deductibles[id] = { ...deductibles[id], core: option_value };
      setDeductibles({ ...deductibles });
    });

    setViewSelectedOption(id, 'core_with_op_deductible', (option_value) => {
      deductibles[id] = { ...deductibles[id], core_with_op: option_value };
      setDeductibles({ ...deductibles });
    });

    setViewSelectedOption(id, 'pulse_deductible', (option_value) => {
      deductibles[id] = { ...deductibles[id], pulse: option_value };
      setDeductibles({ ...deductibles });
    });

    setViewSelectedOption(id, 'complete_deductible', (option_value) => {
      deductibles[id] = { ...deductibles[id], complete: option_value };
      setDeductibles({ ...deductibles });
    });

    setViewSelectedOption(id, 'room_type', (option_value) => {
      pacificCrossPrimaryCarePlanSpecific['room_type'] = option_value;
      setPacificCrossPrimaryCarePlanSpecific({ ...pacificCrossPrimaryCarePlanSpecific });
    });

    setViewSelectedOption(id, 'high_cost_country_plan', (option_value) => {
      pacificCrossPrimaryCarePlanSpecific['high_cost_country_plan'] = option_value == 1 || option_value === true;
      setPacificCrossPrimaryCarePlanSpecific({ ...pacificCrossPrimaryCarePlanSpecific });
    });

    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });

    setViewSelectedOption(id, 'maternity_5000', (option_value) => {
      const checked = option_value == 1 || option_value === true;
      setOptionalBenefits(prev => ({ ...prev, maternity_5000: checked, maternity_10000: checked ? false : prev.maternity_10000 }));
    });

    setViewSelectedOption(id, 'maternity_10000', (option_value) => {
      const checked = option_value == 1 || option_value === true;
      setOptionalBenefits(prev => ({ ...prev, maternity_10000: checked, maternity_5000: checked ? false : prev.maternity_5000 }));
    });

    setViewSelectedOption(id, 'dental', (option_value) => {
      setOptionalBenefits(prev => ({ ...prev, dental: option_value == 1 || option_value === true }));
    });

    setViewSelectedOption(id, 'optical', (option_value) => {
      setOptionalBenefits(prev => ({ ...prev, optical: option_value == 1 || option_value === true }));
    });
  }, [selectedOptions]);

  // One entry per plan, so the Premiums Summary can compare this insurer's plans against
  // the other insurers' - each entry is what the client actually pays for that plan.
  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;

    const _computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const computedRate = {
        'client': rates[i].client,
        'currency': rates[i].currency
      };

      plans.forEach(({ plan }) => {
        computedRate[plan] = getFinalPremium(i, plan);
      });

      _computedRates.push(computedRate);
    }

    return _computedRates;
  }

  function getRate(clientIndex, benefit) {
    if (!response) return NO_RATE;

    if (!response.rates[clientIndex]) return NO_RATE;

    const rate = response.rates[clientIndex][benefit];

    if (rate === undefined || rate === null) return NO_RATE;

    return rate;
  }

  // 'Unavailable' (or any other note the rate sheet carries) has to survive every sum
  // untouched, so a premium built on top of it is never shown as a number.
  function isRateAvailable(rate) {
    return !(rate === '' || rate === null || rate === undefined || typeof rate === 'string' || isNaN(rate));
  }

  function getSelectedPlans() {
    return plans.filter(({ planId }) => selectedPlans.includes(planId));
  }

  // The plan premium before any discount - the sum of every plan type ticked above.
  function getPremium(clientIndex) {
    const _selectedPlans = getSelectedPlans();

    if (_selectedPlans.length === 0) return 0;

    let total = 0;

    for (let i = 0; i < _selectedPlans.length; i++) {
      const rate = getRate(clientIndex, _selectedPlans[i].plan);

      if (!isRateAvailable(rate)) return rate;

      total += rate;
    }

    return total;
  }

  function getFamilyDiscountRate() {
    const clients_count = clients.length;

    if (clients_count === 0) return 0;

    if (clients_count >= 4) {
      return family_discounts['>4'] || 0;
    }

    return family_discounts[clients_count] || 0;
  }

  function getRenewalPercent() {
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;

    return (100 - discount + loading) * 0.01;
  }

  function getPremiumAfterDiscount(clientIndex, plan = null) {
    const premium = plan ? getRate(clientIndex, plan) : getPremium(clientIndex);

    if (!isRateAvailable(premium)) return premium;

    return premium * (1 - getFamilyDiscountRate()) * getRenewalPercent();
  }

  function getOptionalRate(clientIndex, benefit) {
    if (benefit === 'maternity_5000' || benefit === 'maternity_10000') {
      if (!optionalBenefits[benefit]) return 0;

      return getRate(clientIndex, benefit);
    }

    if (benefit === 'dental') {
      if (!optionalBenefits.dental) return 0;

      return getRate(clientIndex, 'dental');
    }

    if (benefit === 'optical') {
      if (!optionalBenefits.optical) return 0;

      return getRate(clientIndex, 'vision');
    }

    return 0;
  }

  function getOptionalPremium(clientIndex) {
    let total = 0;

    ['maternity_5000', 'maternity_10000', 'dental', 'optical'].forEach(benefit => {
      const rate = getOptionalRate(clientIndex, benefit);

      if (isRateAvailable(rate)) {
        total += rate;
      }
    });

    return total;
  }

  // Optional benefits are flat rates, so they sit outside the family/renewal discount.
  function getFinalPremium(clientIndex, plan = null) {
    const premium = getPremiumAfterDiscount(clientIndex, plan);

    if (!isRateAvailable(premium)) return premium;

    return premium + getOptionalPremium(clientIndex);
  }

  function getSubtotal(getRateForClient) {
    let subTotal = 0;

    for (let i = 0; i < clients.length; i++) {
      const rate = getRateForClient(i);

      if (!isRateAvailable(rate)) return rate;

      subTotal += rate;
    }

    return subTotal;
  }

  // Only Annually is quoted by the rate sheet, so the other frequencies are derived here
  // from the surcharges the rate sheet's config carries.
  function getPremiumPerPaymentFrequency(payment_frequency, clientIndex = null, plan = null) {
    const annual = clientIndex === null
      ? getSubtotal(idx => getFinalPremium(idx, plan))
      : getFinalPremium(clientIndex, plan);

    if (!isRateAvailable(annual)) return annual;

    const surcharge = payment_frequency_charges[payment_frequency] || 0;

    if (payment_frequency === 'Semi Annually') {
      return annual + (annual * surcharge);
    }

    const installments = {
      'Annually': 1,
      'Quarterly': 4,
      'Monthly': 12
    }[payment_frequency] || 1;

    return (annual + (annual * surcharge)) / installments;
  }

  // Matches the rate sheet's labeling for the Semi Annually row specifically -
  // other payment frequencies keep their default "+X%" label.
  function getPaymentFrequencyLabel(payment_frequency) {
    const percent = ((payment_frequency_charges[payment_frequency] || 0) * 100).toFixed(0);

    if (payment_frequency === 'Semi Annually') {
      return `Semi Annual Premium (${percent}%)`;
    }

    return `${payment_frequency} Premium (+${percent}%)`;
  }

  function getCurrency() {
    if (!response) return specificCurrency[id];

    if (!response.rates || response.rates.length === 0) return specificCurrency[id];

    return response.rates[0].currency;
  }

  function getFamilyDiscountLabel() {
    return `${(getFamilyDiscountRate() * 100).toFixed(0)}%`;
  }

  function getAreaOfCoverage() {
    return specificAreaOfCoverage[id] || AREA_OF_COVERAGE_WITHOUT_USA;
  }

  function handleSpecificChange(key, value) {
    pacificCrossPrimaryCarePlanSpecific[key] = value;
    setPacificCrossPrimaryCarePlanSpecific({ ...pacificCrossPrimaryCarePlanSpecific });

    addToPendingTasks([id]);
  }

  const optionalRows = [
    { key: 'maternity_5000', label: 'Maternity 5K', visible: optionalBenefits.maternity_5000 },
    { key: 'maternity_10000', label: 'Maternity 10K', visible: optionalBenefits.maternity_10000 },
    { key: 'dental', label: 'Dental', visible: optionalBenefits.dental },
    { key: 'optical', label: 'Optical', visible: optionalBenefits.optical }
  ].filter(row => row.visible);

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2, scrollMarginTop: '200px' }} variant='outlined' ref={containerRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1565c0', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#1565c0',
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            lineHeight: 1.2,
            height: 60
          }}>Pacific Cross PrimaryCare Plan</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: '#f8f9fa', px: 4 }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/pacific-cross-logo.avif`}
              alt="Pacific Cross"
              style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none', transform: 'scale(1.5)' }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>

          <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
              {
                plans.map(({ planId, label }) => (
                  <Box key={planId} sx={{ height: 32, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                      control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={planId} />}
                      label={label}
                    />
                  </Box>
                ))
              }
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Deductible</Typography>
              {
                plans.map(({ planId, plan }) => (
                  <Box key={planId} sx={{ height: 32, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                    <CustomSelect2
                      dense
                      sx={{ width: 140 }}
                      value={deductibles[id]?.[plan] || ''}
                      onChange={(e) => {
                        setDeductibles({ ...deductibles, [id]: { ...deductibles[id], [plan]: e.target.value } });

                        addToPendingTasks([id]);
                      }}
                      items={getDeductibleItems(plan)}
                      disabled={loadingStatus[id]}
                    />
                  </Box>
                ))
              }
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Optional Benefits</Typography>
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={
                  <Checkbox
                    size="small"
                    checked={optionalBenefits.maternity_5000}
                    onChange={(e) => setOptionalBenefits({ ...optionalBenefits, maternity_5000: e.target.checked, maternity_10000: e.target.checked ? false : optionalBenefits.maternity_10000 })}
                    disabled={loadingStatus[id]}
                  />
                }
                label="Maternity 5K"
              />
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={
                  <Checkbox
                    size="small"
                    checked={optionalBenefits.maternity_10000}
                    onChange={(e) => setOptionalBenefits({ ...optionalBenefits, maternity_10000: e.target.checked, maternity_5000: e.target.checked ? false : optionalBenefits.maternity_5000 })}
                    disabled={loadingStatus[id]}
                  />
                }
                label="Maternity 10K"
              />
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={
                  <Checkbox
                    size="small"
                    checked={optionalBenefits.dental}
                    onChange={(e) => setOptionalBenefits({ ...optionalBenefits, dental: e.target.checked })}
                    disabled={loadingStatus[id]}
                  />
                }
                label="Dental Care"
              />
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={
                  <Checkbox
                    size="small"
                    checked={optionalBenefits.optical}
                    onChange={(e) => setOptionalBenefits({ ...optionalBenefits, optical: e.target.checked })}
                    disabled={loadingStatus[id]}
                  />
                }
                label="Optical Care"
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {/* <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id] || 'USD'}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["USD"].map(_currency => (
                      <MenuItem key={_currency} value={_currency}>{_currency}</MenuItem>
                    ))
                  }
                </Select>
              </Box>
              */}

              {/*<Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id] || 'Annually'}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Annually"].map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency}>{payment_frequency}</MenuItem>
                    ))
                  }
                </Select>
              </Box>
              */}

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts[id] ?? 0}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={loadings[id] ?? 0}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 5, mt: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 190 }}>Semi-Private Room (HK)</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 170 }}
                  value={hasHongKongClient && pacificCrossPrimaryCarePlanSpecific['room_type'] === 'SPR' ? 'Yes' : 'No'}
                  onChange={(e) => {
                    handleSpecificChange('room_type', e.target.value === 'Yes' ? 'SPR' : 'Private');
                  }}
                  items={['No', 'Yes']}
                  disabled={loadingStatus[id] || !hasHongKongClient}
                  isZeroDeductibleUniform={false}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 190 }}>High-Cost Country Plan</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 170 }}
                  value={pacificCrossPrimaryCarePlanSpecific['high_cost_country_plan'] ? 'Yes' : 'No'}
                  onChange={(e) => {
                    handleSpecificChange('high_cost_country_plan', e.target.value === 'Yes');
                  }}
                  items={['No', 'Yes']}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 190 }}>Area of Coverage</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 170 }}
                  value={getAreaOfCoverage()}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  items={[AREA_OF_COVERAGE_WITHOUT_USA, AREA_OF_COVERAGE_WITH_USA]}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 5, mt: 2, flexWrap: 'wrap' }}>
            <Typography fontSize={13}>
              Family Discount: <span style={{ fontSize: 13, color: '#1565c0', fontWeight: 600 }}>{getFamilyDiscountLabel()}</span>
            </Typography>
          </Box>

          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7, marginTop: 24 }}>
            <CopyExcelDataButton
              elementId='table-pacific-cross-primary-care-plan-rates'
              style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
            <CopyImageButton
              elementId='table-pacific-cross-primary-care-plan-rates'
              setShowImageCopy={setShowImageCopy}
              style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table id='table-pacific-cross-primary-care-plan-rates' size='small' style={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#1565c0' }}>
                  <TableCell style={{ width: 260, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}>
                        <div>{client.full_name} ({client.age})</div>
                        <div>{client.country_of_residence}</div>
                      </TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  plans.map(({ planId, plan, label }) => {
                    if (!selectedPlans.includes(planId)) return null;

                    return (
                      <TableRow key={planId}>
                        <TableCellText content={label} style={{ width: 260, minWidth: 260 }} />
                        {
                          clients.map((client, idx) => (
                            <TableCellRate
                              key={client.uid}
                              rate={getRate(idx, plan)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                            />
                          ))
                        }
                        <TableCellRate
                          rate={getSubtotal(clientIndex => getRate(clientIndex, plan))}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                        />
                      </TableRow>
                    );
                  })
                }

                {
                  getFamilyDiscountRate() > 0 &&
                  getSelectedPlans().map(({ planId, plan, label }) => (
                    <TableRow key={planId}>
                      <TableCellText content={`${label} Premium after discount`} style={{ fontWeight: 600 }} bgColor='#ffcc80' />
                      {
                        clients.map((client, idx) => (
                          <TableCellRate
                            key={client.uid}
                            rate={getPremiumAfterDiscount(idx, plan)}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#ffcc80'
                          />
                        ))
                      }
                      <TableCellRate
                        rate={getSubtotal(clientIndex => getPremiumAfterDiscount(clientIndex, plan))}
                        currency={getCurrency()}
                        isLoading={loadingStatus[id]}
                        bgColor='#ffcc80'
                      />
                    </TableRow>
                  ))
                }

                {
                  optionalRows.length > 0 &&
                  <TableRow>
                    <TableCell colSpan={clients.length + 2} style={{ fontWeight: 600, background: '#1565c0', color: '#fff' }}>Optional Premium</TableCell>
                  </TableRow>
                }

                {
                  optionalRows.map(({ key, label }) => (
                    <TableRow key={key}>
                      <TableCellText content={label} style={{ width: 260, minWidth: 260 }} />
                      {
                        clients.map((client, idx) => (
                          <TableCellRate
                            key={client.uid}
                            rate={getOptionalRate(idx, key)}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                          />
                        ))
                      }
                      <TableCellRate
                        rate={getSubtotal(clientIndex => getOptionalRate(clientIndex, key))}
                        currency={getCurrency()}
                        isLoading={loadingStatus[id]}
                      />
                    </TableRow>
                  ))
                }

                {/* One Annual + payment frequency block per ticked plan, separated by a blank row. */}
                {
                  getSelectedPlans().map(({ planId, plan, label }, planIndex) => (
                    <Fragment key={planId}>
                      {
                        planIndex > 0 &&
                        <TableRow>
                          <TableCell colSpan={clients.length + 2} style={{ height: 20, padding: 0, background: '#fff' }} />
                        </TableRow>
                      }

                      <TableRow>
                        <TableCellText content={`${label} Annual Premium`} style={{ fontWeight: 600 }} bgColor='#ffcc80' />
                        {
                          clients.map((client, idx) => (
                            <TableCellRate
                              key={client.uid}
                              rate={getFinalPremium(idx, plan)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#ffcc80'
                            />
                          ))
                        }
                        <TableCellRate
                          rate={getSubtotal(clientIndex => getFinalPremium(clientIndex, plan))}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#ffcc80'
                        />
                      </TableRow>

                      {
                        Object.keys(payment_frequency_charges).map(payment_frequency => (
                          <TableRow key={payment_frequency}>
                            <TableCellText
                              content={`${label} ${getPaymentFrequencyLabel(payment_frequency)}`}
                              style={{ fontWeight: 600 }}
                              bgColor='#ffcc80'
                            />
                            {
                              clients.map((client, idx) => (
                                <TableCellRate
                                  key={client.uid}
                                  rate={getPremiumPerPaymentFrequency(payment_frequency, idx, plan)}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                  bgColor='#ffcc80'
                                />
                              ))
                            }
                            <TableCellRate
                              rate={getPremiumPerPaymentFrequency(payment_frequency, null, plan)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#ffcc80'
                            />
                          </TableRow>
                        ))
                      }
                    </Fragment>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>

          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>
      </Paper>
    </Box>
  );
});

export default PacificCrossPrimaryCarePlan_Rates;
